<template>
    <section v-if="opened" class="timesheet-cell-picker" :class="{'timesheet-cell-picker--inline': inline}" :style="inline ? {} : position" :role="inline ? 'region' : typing ? 'listbox' : 'dialog'"
        aria-label="Отметки в выделенных ячейках" @mousedown.stop @keydown.esc.prevent.stop="close(true)" @toggle.capture="schedulePosition">
        <template v-if="typing">
            <div class="timesheet-cell-picker__options">
                <button v-for="(option, index) in options" :key="option.value" type="button" role="option"
                    :aria-selected="suggestionIndex === index ? 'true' : 'false'"
                    :class="{'is-active': suggestionIndex === index}" @mousedown.prevent
                    @click="query = option.value; apply(false)"><b>{{ option.value }}</b> {{ option.label }}</button>
                <small v-if="!options.length">Нет подходящих вариантов</small>
            </div>
            <small>↑↓ — выбрать · Enter — записать и перейти вниз · Esc — закрыть</small>
        </template>
        <template v-else>
        <header :class="{'timesheet-cell-picker__move': !inline}" :title="inline ? null : 'Перетащите за заголовок, чтобы переместить окно'" @pointerdown="startPanelMove"><strong>{{ api.canEdit ? 'Отметки в выделении' : 'Сводка выделения' }}</strong>
            <button type="button" aria-label="Закрыть выбор отметки" @click="close(true)">×</button></header>
        <small>{{ caption }}</small>
        <div class="timesheet-cell-picker__summary" aria-live="polite">
            <strong>Ячеек: {{ summary.count }} · Часов: {{ formatNumber(summary.minutes / 60) }}</strong>
            <strong>Шифр: {{ summary.cipher }}{{ summary.invalid.length ? ' (неполный итог)' : '' }}</strong>
        </div>
        <details><summary>Расшифровка отметок</summary>
            <div v-for="item in summary.details" :key="item.key">{{ item.label }}: <b>{{ formatNumber(item.value) }}</b></div>
        </details>
        <small v-if="summary.invalid.length" role="alert" class="text-danger">Не удалось посчитать ячейки: {{ summary.invalid.length }}. Исправьте их значения; они исключены из часов и шифра.</small>
        <small v-if="summary.external">Дт и часы других табелей не включены. Ячеек с данными в других табелях: {{ summary.external }}.</small>
        <small v-if="hiddenCount">В выделении скрытых дней: {{ hiddenCount }}.{{ api.canEdit ? ' Отметка будет применена и к ним.' : '' }}</small>
        <form v-if="api.canEdit" @submit.prevent="apply(true)">
            <input ref="query" :value="query" :disabled="api.batchEditing" @input="queryInput" @compositionend="queryInput" type="text" inputmode="decimal" autocomplete="off"
                class="form-control form-control-sm" aria-label="Значение для выделенных ячеек" placeholder="Часы или обозначение">
            <div class="timesheet-cell-picker__options">
                <button v-for="option in options" :key="option.value" type="button"
                    @click="query = option.value; $refs.query.focus()"><b>{{ option.value }}</b> {{ option.label }}</button>
            </div>
            <small v-if="replaced">Будут заменены заполненные ячейки: {{ replaced }}.</small>
            <footer><div v-if="error" role="alert" class="text-danger w-100">{{ error }}</div>
                <button type="button" class="btn btn-sm btn-outline-secondary" title="Удалить отметки в выделенных ячейках"
                    @click="query = ''; apply(false, true)">Очистить ячейки</button>
                <button type="submit" class="btn btn-sm btn-primary" :disabled="!query.trim() || api.batchEditing || api.editErrorCount > 0">Применить</button></footer>
        </form>
        </template>
    </section>
</template>

<script>
import {rectangleKeys, coordinate} from '@/utils/timesheetCellSelection';
import {isTimesheetValuePrefix} from '@/utils/timesheetValueInput';
import {selectionSummary} from '@/utils/timesheetSelectionSummary';
import {number} from '@/utils/timesheetDaySummary';
import {pickerPosition} from '@/utils/timesheetPickerPosition';

export default {
    name: 'TimesheetCellPicker',
    props: {api: {type: Object, required: true}, inline: Boolean, summaryProvider: Function, preserveSelectionOnReorder: Boolean},
    data: () => ({opened: false, typing: false, suggestionIndex: -1, query: '', error: '', count: 0, caption: '', position: {}, selectedKeys: []}),
    computed: {
        summary() { return this.summaryProvider ? this.summaryProvider(this.selectedKeys) : selectionSummary(this.api, this.selectedKeys); },
        hiddenCount() { return this.selectedKeys.filter(key => !this.api.keyboardDates.includes(coordinate(key).date)).length; },
        options() { return this.opened ? this.api.filteredCellSuggestions(this.query).slice(0, 30) : []; },
        replaced() {
            if (!this.opened) return 0;
            return Array.from(this._keys).filter(key => this.api.monthCellDrafts[key]
                && this.api.monthCellDrafts[key] !== this.query).length;
        }
    },
    watch: {
        'api.keyboardRows'(next, previous) {
            if (next.join('|') === previous.join('|')) return;
            if (this.preserveSelectionOnReorder && next.length === previous.length && next.every(id => previous.includes(id))) return;
            this.clear();
        },
        'api.keyboardDates'(next, previous) { if (!this._fullMonth && next.join('|') !== previous.join('|')) this.clear(); },
        'api.periodMonth'() { this.clear(); },
        'api.sheet.timesheet.id'() { this.clear(); },
        'api.canEdit'(allowed) { if (!allowed) this.clear(); }
    },
    created() {
        this._nodes = new Map(); this._keys = new Set(); this._anchor = null; this._end = null;
        this.api._cellPicker = this;
    },
    mounted() {
        this._root = this.api.$el || this.$el.parentNode;
        this._root.addEventListener('mousedown', this.start);
        this._root.addEventListener('dblclick', this.doubleClick);
        this._root.addEventListener('keydown', this.keydown, true);
        this._root.addEventListener('click', this.captureClick, true);
        document.addEventListener('mousedown', this.outside);
        window.addEventListener('mousemove', this.move);
        window.addEventListener('mouseup', this.finish);
        window.addEventListener('blur', this.clear);
        window.addEventListener('resize', this.clear);
        window.addEventListener('scroll', this.handlePositionScroll, true);
    },
    updated() { this.schedulePosition(); },
    beforeDestroy() {
        this.clear();
        this._root.removeEventListener('mousedown', this.start);
        this._root.removeEventListener('dblclick', this.doubleClick);
        this._root.removeEventListener('keydown', this.keydown, true);
        this._root.removeEventListener('click', this.captureClick, true);
        document.removeEventListener('mousedown', this.outside);
        window.removeEventListener('mousemove', this.move);
        window.removeEventListener('mouseup', this.finish);
        window.removeEventListener('blur', this.clear);
        window.removeEventListener('resize', this.clear);
        window.removeEventListener('scroll', this.handlePositionScroll, true);
        if (this.api._cellPicker === this) this.api._cellPicker = null;
    },
    methods: {
        handlePositionScroll(event) {
            if (event.target instanceof Node && this.$el.contains(event.target)) return;
            this.schedulePosition();
        },
        schedulePosition() {
            if (!this.opened || this.inline || this._panelDetached || this._positionFrame) return;
            this._positionFrame = requestAnimationFrame(() => {
                this._positionFrame = null;
                if (!this.opened || this.inline || this._panelDetached || !this._popupAnchor) return;
                if (!this._popupAnchor.isConnected) { this.clear(); return; }
                const rect = this._popupAnchor.getBoundingClientRect();
                if (rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) {
                    this.clear(); return;
                }
                const position = pickerPosition(rect,
                    {width: this.$el.getBoundingClientRect().width, height: this.$el.scrollHeight + 2},
                    {width: window.innerWidth, height: window.innerHeight});
                if (Object.keys(position).some(key => position[key] !== this.position[key])) this.position = position;
            });
        },
        startPanelMove(event) {
            if (this.inline || this.typing || event.button !== 0 || event.target.closest('button')) return;
            event.preventDefault();
            event.stopPropagation();
            this.stopPanelMove();
            this._panelDetached = true;
            const rect = this.$el.getBoundingClientRect();
            this._panelMove = {pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height};
            window.addEventListener('pointermove', this.movePanel);
            window.addEventListener('pointerup', this.stopPanelMove);
            window.addEventListener('pointercancel', this.stopPanelMove);
        },
        movePanel(event) {
            const drag = this._panelMove;
            if (!drag || event.pointerId !== drag.pointerId) return;
            const width = document.documentElement.clientWidth, height = document.documentElement.clientHeight;
            const left = Math.max(8, Math.min(width - drag.width - 8, drag.left + event.clientX - drag.x));
            const top = Math.max(8, Math.min(height - drag.height - 8, drag.top + event.clientY - drag.y));
            this.position = {...this.position, left: `${left}px`, top: `${top}px`, maxHeight: `${height - top - 8}px`};
        },
        stopPanelMove() {
            if (!this._panelMove) return;
            this._panelMove = null;
            window.removeEventListener('pointermove', this.movePanel);
            window.removeEventListener('pointerup', this.stopPanelMove);
            window.removeEventListener('pointercancel', this.stopPanelMove);
        },
        formatNumber: number,
        captureClick(event) {
            if (!this._suppressClick) return;
            this._suppressClick = false;
            if (event.detail && Date.now() - this._selectionFinishedAt < 500) { event.preventDefault(); event.stopPropagation(); }
        },
        focusCell(lineId, date) {
            if (this.api.focusTimesheetCell) return this.api.focusTimesheetCell(lineId, date);
            const node = Array.from(this._nodes.get(this.api.cellKey(lineId, date)) || []).find(item => item.getClientRects().length);
            if (node) node.focus({preventScroll: true});
        },
        selectMonth(line, node) {
            this.clear();
            const dates = this.api.monthDays.map(day => day.date);
            if (!dates.length) return;
            this._fullMonth = true;
            this._anchor = {lineId: String(line.id), date: dates[0]};
            this._end = {lineId: String(line.id), date: dates[dates.length - 1]};
            this.paint(dates.map(date => this.api.cellKey(line.id, date)));
            this.open(node);
            this.caption = `${line.employee_name_snapshot} · Весь месяц`;
        },
        queryInput(event) {
            if (this.api.batchEditing) { event.target.value = this.query; return; }
            if (event.isComposing) return;
            const bootstrap = this.api.bootstrapData || {};
            if (!isTimesheetValuePrefix(event.target.value, bootstrap.codes || [], (bootstrap.policy || {}).maximum_daily_minutes || 1440)) {
                event.target.value = this.query;
                this.error = 'Допустимы только часы и обозначения табеля.';
                return;
            }
            this.query = event.target.value; this.error = '';
        },
        register(key, node) {
            if (!this._nodes.has(key)) this._nodes.set(key, new Set());
            this._nodes.get(key).add(node);
            node.classList.toggle('is-range-selected', this._keys.has(key));
        },
        unregister(key, node) {
            const nodes = this._nodes.get(key);
            if (nodes) { nodes.delete(node); if (!nodes.size) this._nodes.delete(key); }
        },
        cell(target) {
            const node = target && target.closest && target.closest('.timesheet-keyboard-cell[data-work-date]');
            if (!node || !this._root.contains(node) || node.disabled) return null;
            return {node, lineId: node.dataset.lineId, date: node.dataset.workDate};
        },
        paint(keys) {
            const next = new Set(keys);
            const changed = new Set([...this._keys, ...next]);
            changed.forEach(key => {
                if (this._keys.has(key) === next.has(key)) return;
                (this._nodes.get(key) || []).forEach(node => node.classList.toggle('is-range-selected', next.has(key)));
            });
            this._keys = next;
            this.selectedKeys = Array.from(next);
            this.count = next.size;
            if (this.api._gridFrame) this.api._gridFrame.setSelection(next);
        },
        rectangle(end) {
            this._end = {lineId: end.lineId, date: end.date};
            this.paint([...(this._base || []), ...rectangleKeys(this.api.keyboardRows, this.api.keyboardDates, this._anchor, this._end)]);
        },
        start(event) {
            const cell = this.cell(event.target);
            if (!cell || event.button !== 0) return;
            this._suppressClick = false;
            this._fullMonth = false;
            // Cancel native text selection/drag even when this input already has
            // selected text. Mouse movement belongs to the cell range instead.
            event.preventDefault();
            if (!event.shiftKey && !event.ctrlKey && !event.metaKey) {
                cell.node.focus({preventScroll: true});
                if (cell.node.select) cell.node.select();
            }
            this.opened = false;
            const key = this.api.cellKey(cell.lineId, cell.date);
            this._base = event.ctrlKey || event.metaKey ? Array.from(this._keys) : [];
            this._drag = {x: event.clientX, y: event.clientY, moved: Boolean(event.shiftKey),
                openOnRelease: Boolean(event.ctrlKey || event.metaKey), node: cell.node};
            if (!(event.shiftKey && this._anchor)) this._anchor = {lineId: cell.lineId, date: cell.date};
            if ((event.ctrlKey || event.metaKey) && this._keys.has(key)) {
                this._base = this._base.filter(item => item !== key);
                this.paint(this._base); this._end = this._anchor;
            } else this.rectangle(cell);
        },
        move(event) {
            if (!this._drag) return;
            if (!(event.buttons & 1)) { this.finish(); return; }
            this._point = {x: event.clientX, y: event.clientY};
            if (Math.abs(event.clientX - this._drag.x) + Math.abs(event.clientY - this._drag.y) > 5) this._drag.moved = true;
            if (this._drag.moved) { event.preventDefault(); this.scheduleDrag(); }
        },
        scheduleDrag() {
            if (this._frame) return;
            this._frame = requestAnimationFrame(() => { this._frame = null; this.dragFrame(); });
        },
        dragFrame() {
            if (!this._drag || !this._point) return;
            const viewport = this.api.$refs.monthScroll;
            let {x, y} = this._point;
            let scrolling = false;
            if (viewport && viewport.contains(this._drag.node)) {
                const rect = viewport.getBoundingClientRect();
                const header = viewport.querySelector('thead');
                const top = rect.top + (header ? header.getBoundingClientRect().height : 0);
                const footer = viewport.querySelector('tfoot td');
                const bottom = Math.min(rect.top + viewport.clientHeight, footer ? footer.getBoundingClientRect().top : Infinity);
                const delta = y < top + 20 ? -18 : y > bottom - 20 ? 18 : 0;
                const previous = viewport.scrollTop;
                const previousLeft = viewport.scrollLeft;
                if (delta) viewport.scrollTop += delta;
                const pinned = viewport.querySelector('[data-selection-pinned]');
                const left = Math.max(rect.left, pinned ? pinned.getBoundingClientRect().right : rect.left);
                if (x < left + 20) viewport.scrollLeft -= 18;
                else if (x > rect.right - 20) viewport.scrollLeft += 18;
                scrolling = previous !== viewport.scrollTop || previousLeft !== viewport.scrollLeft;
                y = Math.max(top + 4, Math.min(bottom - 4, y));
                x = Math.max(left + 4, Math.min(rect.right - 4, x));
            }
            const target = document.elementFromPoint(x, y);
            const cell = this.cell(target) || this.cell(target && target.closest('td') && target.closest('td').querySelector('.timesheet-keyboard-cell'));
            if (cell) { this.rectangle(cell); this._drag.node = cell.node; }
            if (scrolling) this.scheduleDrag();
        },
        finish() {
            if (!this._drag) return;
            if (this._frame) { cancelAnimationFrame(this._frame); this._frame = null; this.dragFrame(); }
            const drag = this._drag; this._drag = null; this._point = null; this._base = [];
            if (this._frame) { cancelAnimationFrame(this._frame); this._frame = null; }
            if (drag.moved || drag.openOnRelease) {
                this._suppressClick = true; this._selectionFinishedAt = Date.now();
                if (this._keys.size) this.open(drag.node);
            }
        },
        doubleClick(event) {
            const cell = this.cell(event.target);
            if (cell) { event.preventDefault(); this._anchor = cell; this._base = []; this.rectangle(cell); this.open(cell.node); }
        },
        keydown(event) {
            const cell = this.cell(event.target);
            if (!cell || event.isComposing) return;
            if (event.key === 'Escape') { this.clear(); return; }
            if (this.opened && this.typing && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
                if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && this.options.length) {
                    event.preventDefault(); event.stopPropagation();
                    const delta = event.key === 'ArrowDown' ? 1 : -1;
                    this.suggestionIndex = this.suggestionIndex < 0 ? (delta > 0 ? 0 : this.options.length - 1)
                        : (this.suggestionIndex + delta + this.options.length) % this.options.length;
                    return;
                }
                if (event.key === 'Enter' && this.options[this.suggestionIndex]) {
                    event.preventDefault(); event.stopPropagation();
                    this.query = this.options[this.suggestionIndex].value; this.apply(true); return;
                }
                if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') return;
            }
            if (event.altKey && event.key === 'ArrowDown') {
                event.preventDefault(); event.stopPropagation();
                if (!this._keys.has(this.api.cellKey(cell.lineId, cell.date))) { this._anchor = cell; this._base = []; this.rectangle(cell); }
                this.open(cell.node); return;
            }
            if (event.shiftKey && /^Arrow/.test(event.key)) {
                const row = this.api.keyboardRowIndex.get(cell.lineId);
                const col = this.api.keyboardDateIndex.get(cell.date);
                const lineId = this.api.keyboardRows[row + (event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0)];
                const date = this.api.keyboardDates[col + (event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0)];
                event.preventDefault(); event.stopPropagation();
                if (lineId && date) {
                    if (!this._anchor) this._anchor = cell;
                    this._base = []; this.rectangle({lineId, date});
                    this.focusCell(lineId, date);
                    this.open(cell.node, false, false);
                }
                return;
            }
            // Enter/Tab and ordinary typing never summon or carry a popup to the next cell.
            if (!event.ctrlKey && !event.metaKey && !event.altKey) this.clear();
        },
        outside(event) {
            if (this.$el.contains(event.target) || this.cell(event.target)) return;
            this.clear();
        },
        open(node, typing = false, focus = true) {
            if (!this._keys.size) return;
            const first = coordinate(Array.from(this._keys)[0]);
            const line = this.api.sheet.lines.find(item => String(item.id) === first.lineId);
            const values = Array.from(this._keys).map(key => this.api.monthCellDrafts[key] || '');
            this.query = values.every(value => value === values[0]) ? values[0] : '';
            this.typing = typing; this.suggestionIndex = -1;
            this.count = this._keys.size; this.error = '';
            this.caption = this.count === 1 ? `${line ? line.employee_name_snapshot : ''} · ${first.date}` : this.api.selectionCaption || 'Только ячейки этого табеля';
            this._scope = `${this.api.currentTimesheetId()}:${this.api.periodMonth}`;
            this._popupAnchor = node.closest && node.closest('td, th') || node;
            this._panelDetached = false;
            const rect = this._popupAnchor.getBoundingClientRect();
            this.position = pickerPosition(rect, {width: 320, height: typing ? 260 : 500},
                {width: window.innerWidth, height: window.innerHeight});
            this.opened = true;
            if (!typing && focus) this.$nextTick(() => { if (this.opened && !this.typing && this.api.canEdit && this.$refs.query) { this.$refs.query.focus(); this.$refs.query.select(); } });
        },
        suggestFor(line, date, node, value) {
            if (!this.api.canEdit) return;
            if (!String(value || '').trim()) { this.clear(); return; }
            const key = this.api.cellKey(line.id, date);
            if (this.opened && this.typing && this._keys.size === 1 && this._keys.has(key)) {
                this.query = value; this.suggestionIndex = -1; return;
            }
            this._anchor = {lineId: String(line.id), date}; this._base = [];
            this.rectangle(this._anchor); this.open(node, true);
        },
        openFor(line, date) {
            const key = this.api.cellKey(line.id, date);
            const node = Array.from(this._nodes.get(key) || []).find(item => item.getClientRects().length);
            if (!node) return;
            this._anchor = {lineId: String(line.id), date}; this._base = [];
            this.rectangle(this._anchor); this.open(node);
        },
        async apply(advance, clearExplicit = false) {
            if (this.api.batchEditing) return;
            if (!this.query.trim() && !clearExplicit) return;
            if (!this.api.canEdit || this._scope !== `${this.api.currentTimesheetId()}:${this.api.periodMonth}`) { this.clear(); return; }
            try { this.api.parseMonthCell(this.query); } catch (error) { this.error = error.message; return; }
            const keys = Array.from(this._keys);
            const lines = new Map(this.api.sheet.lines.map(line => [String(line.id), line]));
            const cells = keys.map(key => ({key, ...coordinate(key)}));
            const dates = this._fullMonth ? this.api.monthDays.map(day => day.date) : this.api.keyboardDates;
            if (cells.some(cell => !lines.has(cell.lineId) || !dates.includes(cell.date))) { this.clear(); return; }
            const saved = await this.api.saveCellsBatch(cells.map(cell => ({line: lines.get(cell.lineId), date: cell.date})), this.query);
            if (!saved) {
                this.error = this.api.arrivalCancelledMessage || 'Пакет не записан. При обновлении или закрытии страницы ввод будет потерян. Проверьте сообщения сохранения.';
                return;
            }
            const last = this._end || cells[cells.length - 1];
            const next = cells.length === 1 && advance && this.api.keyboardRows[this.api.keyboardRowIndex.get(last.lineId) + 1];
            this.clear();
            if (last) this.focusCell(next || last.lineId, last.date);
        },
        close(restore) {
            const last = this._end;
            this.clear();
            if (restore && last) this.focusCell(last.lineId, last.date);
        },
        clear() {
            if (!this._isBeingDestroyed && (this.api.batchEditing || this.api.editErrorCount && this.opened && !this.typing)) return;
            this.stopPanelMove();
            if (this._positionFrame) cancelAnimationFrame(this._positionFrame);
            this._positionFrame = null; this._popupAnchor = null; this._panelDetached = false;
            this.opened = false; this.typing = false; this.suggestionIndex = -1;
            this._fullMonth = false;
            this._drag = null; this._point = null; this._base = [];
            if (this._frame) cancelAnimationFrame(this._frame);
            this._frame = null; this._anchor = null; this._end = null;
            this.paint([]);
        }
    }
};
</script>

<style>
.timesheet-page .timesheet-keyboard-cell.is-range-selected { background: #e4efff !important; outline: 2px solid #568bd1; outline-offset: -2px; }
.timesheet-cell-picker__summary { display: flex; flex-direction: column; gap: 4px; padding: 8px; margin: 6px 0; border-radius: 4px; background: #eef5fc; overflow-wrap: anywhere; }
.timesheet-cell-picker details { margin-bottom: 8px; font-size: 12px; }
.timesheet-select-month { border: 0; background: transparent; color: #1769aa; padding: 2px 4px; font-size: 12px; white-space: nowrap; }
.timesheet-cell-picker { position: fixed; z-index: 1050; width: 320px; max-width: calc(100vw - 16px); max-height: calc(100vh - 16px); overflow: auto; padding: 10px; background: white; border: 1px solid #b9c7d8; border-radius: 6px; box-shadow: 0 5px 20px #24364b30; }
.timesheet-cell-picker.timesheet-cell-picker--inline { position: static; width: 100%; max-width: none; max-height: 180px; flex: 0 1 auto; min-height: 80px; box-shadow: none; }
.timesheet-cell-picker header, .timesheet-cell-picker footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.timesheet-cell-picker footer { position: sticky; bottom: -10px; flex-wrap: wrap; background: white; padding: 8px 0; }
.timesheet-cell-picker header button { border: 0; background: transparent; font-size: 22px; line-height: 1; }
.timesheet-cell-picker__move { cursor: move; touch-action: none; user-select: none; }
.timesheet-cell-picker small { display: block; color: #64748b; margin: 5px 0; }
.timesheet-cell-picker__options { max-height: 180px; overflow: auto; margin: 6px 0; }
.timesheet-cell-picker__options button { display: block; width: 100%; text-align: left; background: white; border: 0; padding: 4px 6px; }
.timesheet-cell-picker__options button:hover, .timesheet-cell-picker__options button:focus, .timesheet-cell-picker__options button.is-active { background: #e4efff; }
.timesheet-cell-picker__options b { display: inline-block; min-width: 32px; }
</style>
